import React, { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { ReactComponent as ArrowRightIcon16 } from '@repo/common/assets/icons/16px/icon_16px_arrow-right.svg';
import { ReactComponent as NavigationArrowHomeIcon } from '@repo/common/assets/icons/Home/navigationarrow-home.svg';
import { HOME_V2_IMAGES } from './homeV2Assets';
import { BaseButton, trackCtaClick, useT } from '@repo/common';
import { IS_LEGACY_DOMAIN } from '../../../utils/brand.js';

const BRAND_NAME = IS_LEGACY_DOMAIN ? 'O!ng Lab' : 'Koready';

const SLIDE_CARDS = [
  {
    key: 'know',
    title: 'Know Where You Are',
    description:
      'Diagnose your current readiness for Korea through Pathfinder.',
    image: HOME_V2_IMAGES.section3Img1,
  },
  {
    key: 'move',
    title: 'Move Forward',
    description:
      'Diagnose your current readiness for Korea through Pathfinder.',
    image: HOME_V2_IMAGES.section3Img2,
  },
  {
    key: 'apply',
    title: 'Everything You Need to Apply',
    description:
      'Diagnose your current readiness for Korea through Pathfinder.',
    image: HOME_V2_IMAGES.section3Img3,
  },
  {
    key: 'life',
    title: 'Your Life in Korea',
    description: `${BRAND_NAME} supports adaption and long-term life planning in Korea.`,
    image: HOME_V2_IMAGES.section3Img4,
  },
];

const BUSINESS_SLIDE_CARDS = [
  {
    key: 'promotion',
    title: '검증된 글로벌 인재',
    description: '시장 적합성을 검증하고 커리어 교육을 통해 업무 준비도를 높인 글로벌 인재를 연결합니다. 코레디는 단순 HR플랫폼이 아닌, 준비된 인재를 제공하는 NO.1 외국인 인재 플랫폼이 될 것입니다.',
    image: HOME_V2_IMAGES.section3Img1,
  },
  {
    key: 'hiring-path',
    title: '외국인 채용 경로 안내',
    description: '기업의 상황과 고용 형태에 맞는 외국인 인재 채용 경로를 진단하고 안내합니다. Pathfinder by Koready는 곧 오픈됩니다.',
    image: HOME_V2_IMAGES.section3Img2,
  },
  {
    key: 'first-hire',
    title: '첫 채용 성공 수수료 0원',
    description: '초기 파트너 기업은 첫 번째 공고를 통해 채용이 성사되면 성공 수수료 면제 혜택을 제공해드립니다.',
    image: HOME_V2_IMAGES.section3Img3,
  },
];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getCardStep = (el) => {
  const firstCard = el?.firstElementChild;
  if (!firstCard) return 0;

  const { width } = firstCard.getBoundingClientRect();
  const styles = window.getComputedStyle(el);
  const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;

  return width + gap;
};

const getNearestCardIndex = (el) => {
  const children = Array.from(el?.children || []);
  if (!children.length) return 0;

  const viewportCenter = el.scrollLeft + el.clientWidth / 2;
  return children.reduce((nearestIndex, child, index) => {
    const nearest = children[nearestIndex];
    const childCenter = child.offsetLeft + child.offsetWidth / 2;
    const nearestCenter = nearest.offsetLeft + nearest.offsetWidth / 2;

    return Math.abs(childCenter - viewportCenter) <
      Math.abs(nearestCenter - viewportCenter)
      ? index
      : nearestIndex;
  }, 0);
};

const getScrollLeftForIndex = (el, index) => {
  const child = el?.children?.[index];
  if (!child) return 0;

  if (index === 0) return 0;

  const centeredLeft =
    child.offsetLeft - (el.clientWidth - child.offsetWidth) / 2;
  const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);

  return clamp(centeredLeft, 0, maxScroll);
};

const ArrowIcon = ({ direction = 'right', className = '' }) => (
  <ArrowRightIcon16
    aria-hidden='true'
    className={twMerge(
      'size-4 text-icon-inverse [&_path]:stroke-current',
      direction === 'left' && 'rotate-180',
      className,
    )}
  />
);

const ButtonArrowIcon = ({ className = '' }) => (
  <span
    className={twMerge(
      'flex h-3 w-[14px] shrink-0 items-center justify-center min-[1024px]:h-4 min-[1024px]:w-5',
      className,
    )}
    aria-hidden='true'
  >
    <NavigationArrowHomeIcon className='block size-full text-icon-inverse [&_path]:stroke-current' />
  </span>
);

const CarouselButton = ({ direction, onClick, disabled }) => (
  <BaseButton
    type='text'
    size='s'
    onClick={onClick}
    isDisabled={disabled}
    aria-label={direction === 'left' ? 'Previous card' : 'Next card'}
    className='size-9 desktopL:size-12 min-w-0 shrink-0 border-0 bg-transparent p-[3px] hover:bg-transparent disabled:cursor-not-allowed'
    icon={
      <span
        className={twMerge(
          'flex size-[30px] desktopL:size-[42px] items-center justify-center rounded-full bg-grayscale-gray05 transition-[background-color,opacity]',
          !disabled && 'hover:bg-grayscale-gray06',
        )}
      >
        <ArrowIcon direction={direction} />
      </span>
    }
  />
);

const mobileSlideClass =
  'flex w-full shrink-0 snap-center justify-center tabletH:w-auto tabletH:snap-start tabletH:justify-start';

const ArticleCard = ({ title, description, image }) => {
  const t = useT();
  return (
  <div className={mobileSlideClass}>
    <article className='group relative flex h-[276px] w-full max-w-[328px] shrink-0 flex-col justify-end rounded-[30px] p-0 min-[414px]:h-[272px] min-[414px]:max-w-[352px] tabletH:h-[352px] tabletH:w-[220px] tabletH:max-w-none tabletH:overflow-hidden tabletH:p-6 tabletV:h-[400px] tabletV:w-[310px] tabletV:p-[34px] min-[1180px]:h-[460px] min-[1180px]:w-[356px] min-[1180px]:rounded-[40px] desktopL:h-[600px] desktopL:w-[450px] desktopL:p-[34px]'>
      <div
        aria-hidden='true'
        className='absolute left-[26px] top-1/2 h-[276px] w-[276px] -translate-y-1/2 overflow-hidden rounded-[30px] min-[414px]:left-10 min-[414px]:h-[272px] min-[414px]:w-[272px] tabletH:inset-0 tabletH:size-full tabletH:translate-y-0 tabletH:rounded-[inherit]'
      >
        <div className='absolute inset-0 rounded-[inherit] bg-grayscale-gray01' />
        <img
          src={image}
          alt=''
          className='absolute inset-0 size-full rounded-[inherit] object-cover'
        />
        <div className='absolute inset-0 rounded-[inherit] bg-gradient-to-b from-transparent via-transparent to-[rgba(18,18,18,0.5)]' />
        <div className='absolute inset-0 rounded-[inherit] bg-surface-black60 opacity-100 transition-opacity duration-300 tabletH:opacity-0 tabletH:group-hover:opacity-100 tabletH:group-focus-within:opacity-100' />
      </div>

      <div
        className={twMerge(
          'relative z-[1] ml-[26px] flex w-[276px] flex-col gap-2 px-6 pb-6 transition-transform duration-300 ease-out min-[414px]:ml-10 min-[414px]:w-[272px] tabletH:ml-0 tabletH:w-full tabletH:px-0 tabletH:pb-0 tabletV:gap-4',
          description &&
            'group-hover:translate-y-0 group-focus-within:translate-y-0 tabletH:translate-y-[34px] tabletV:translate-y-8 min-[1180px]:translate-y-10',
        )}
      >
        <h3 className='text-[16px] font-medium leading-[1.5] text-text-inverse min-[414px]:text-[18px] tabletV:text-[24px] tabletV:leading-[1.2] min-[1180px]:text-[26px] desktopL:text-[28px]'>
          {t(title)}
        </h3>
        {description ? (
          <p className='text-[12px] font-normal leading-[1.4] text-grayscale-gray00 opacity-100 transition-opacity duration-300 tabletH:opacity-0 tabletH:group-hover:opacity-100 tabletH:group-focus-within:opacity-100 tabletV:text-[14px] min-[1180px]:text-[16px] min-[1180px]:leading-[1.5]'>
            {t(description)}
          </p>
        ) : null}
      </div>
    </article>
  </div>
);
};

const SustainableCareer = ({ isBusiness = false }) => {
  const t = useT();
  const scrollerRef = useRef(null);
  const slideCards = isBusiness ? BUSINESS_SLIDE_CARDS : SLIDE_CARDS;
  const mobileCardCount = slideCards.length + 1;
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeMobileIndex, setActiveMobileIndex] = useState(0);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft < maxScroll - 1);

    setActiveMobileIndex(
      clamp(getNearestCardIndex(el), 0, mobileCardCount - 1),
    );
  }, [mobileCardCount]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;

    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const scroll = useCallback((direction) => {
    const el = scrollerRef.current;
    if (!el) return;

    const isMobileSlider = window.matchMedia('(max-width: 767px)').matches;

    if (!isMobileSlider) {
      const cardStep = getCardStep(el);
      if (!cardStep) return;

      const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
      const nextLeft =
        direction === 'left'
          ? el.scrollLeft - cardStep
          : el.scrollLeft + cardStep;

      el.scrollTo({
        left: clamp(nextLeft, 0, maxScroll),
        behavior: 'smooth',
      });
      return;
    }

    const currentIndex = getNearestCardIndex(el);
    const nextIndex =
      direction === 'left' ? currentIndex - 1 : currentIndex + 1;

    el.scrollTo({
      left: getScrollLeftForIndex(
        el,
        clamp(nextIndex, 0, el.children.length - 1),
      ),
      behavior: 'smooth',
    });
  }, []);

  const scrollToIndex = useCallback((index) => {
    const el = scrollerRef.current;
    if (!el) return;

    el.scrollTo({
      left: getScrollLeftForIndex(el, index),
      behavior: 'smooth',
    });
  }, []);

  return (
    <section className='bg-bg-twodays px-4 pb-20 pt-4 mobile:pb-[120px] tabletH:px-6 tabletH:py-[60px] min-[1024px]:px-10 min-[1024px]:py-[80px] min-[1180px]:px-20 min-[1180px]:py-[120px] desktopL:py-[160px]'>
      <div className='ml-0 flex w-full max-w-none flex-col items-center tabletH:items-start desktopL:ml-auto desktopL:max-w-[1840px]'>
        <div className='flex w-full items-center justify-center gap-4 px-6 pb-6 tabletH:justify-between tabletH:px-2.5 tabletH:pb-7 min-[1024px]:px-[15px] min-[1024px]:pb-9 min-[1180px]:px-[30px] min-[1180px]:pb-10'>
          <h2 className='min-w-0 whitespace-nowrap text-center text-[16px] font-medium leading-[1.5] text-text-primary min-[414px]:text-[20px] min-[414px]:leading-[1.2] tabletH:text-[22px] tabletH:leading-[1.2] min-[1024px]:text-[26px] min-[1180px]:text-[28px] desktopL:text-[40px]'>
            {isBusiness ? (
              '코레디가 준비한 글로벌 인재는 다릅니다'
            ) : (
              <>
                <span className='block tabletH:hidden'>
                  {t('Build a Sustainable')}
                </span>
                <span className='block tabletH:hidden'>
                  {t('Career & Life in Korea')}
                </span>
                <span className='hidden tabletH:inline'>
                  {t('Build a Sustainable Career & Life in Korea')}
                </span>
              </>
            )}
          </h2>
          <div className='items-center hidden gap-3 shrink-0 tabletH:flex'>
            <CarouselButton
              direction='left'
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
            />
            <CarouselButton
              direction='right'
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
            />
          </div>
        </div>

        <div
          ref={scrollerRef}
          className='sustainable-career-scroller relative box-border flex h-[328px] w-full max-w-full snap-x snap-mandatory touch-pan-x overflow-x-auto overflow-y-hidden overscroll-x-contain items-center gap-4 px-4 scroll-smooth scrollbar-hide min-[414px]:h-[352px] tabletH:left-1/2 tabletH:h-[352px] tabletH:w-screen tabletH:max-w-none tabletH:-translate-x-1/2 tabletH:snap-none tabletH:items-start tabletH:gap-4 tabletH:px-6 min-[1024px]:h-[400px] min-[1024px]:gap-[18px] min-[1024px]:px-10 min-[1180px]:h-[460px] min-[1180px]:gap-6 min-[1180px]:px-20 desktopL:h-[600px]'
          style={{ scrollbarWidth: 'none' }}
        >
          <div className={mobileSlideClass}>
            <article className='relative flex h-[328px] w-full max-w-[328px] shrink-0 flex-col items-end justify-between overflow-hidden rounded-[30px] bg-gradient-to-b from-green-light03 from-[20%] to-green-deep01 p-6 min-[414px]:size-[352px] min-[414px]:max-w-[352px] tabletH:size-[352px] tabletH:max-w-none min-[1024px]:size-[352px] min-[1024px]:p-[34px] min-[1180px]:size-[460px] min-[1180px]:rounded-[40px] desktopL:size-[600px]'>
              <img
                src={HOME_V2_IMAGES.cloverGreen}
                alt=''
                aria-hidden
                className='pointer-events-none absolute left-[149.73px] top-[21.65px] size-[156.871px] select-none blur-[5px] min-[414px]:left-[160.69px] min-[414px]:top-[23.23px] min-[414px]:size-[168.35px] min-[1024px]:left-[182.61px] min-[1024px]:top-[26.4px] min-[1024px]:size-[191.305px] min-[1180px]:left-[210px] min-[1180px]:top-[30px] min-[1180px]:size-[220px] desktopL:left-[273.92px] desktopL:top-[39.13px] desktopL:size-[286.956px]'
              />
              <div
                aria-hidden='true'
                className='absolute inset-0 rounded-[inherit]'
                style={{
                  background:
                    'radial-gradient(50% 50% at 50% 50%, rgba(255, 255, 255, 0.05) 40%, rgba(255, 255, 255, 0.40) 100%)',
                }}
              />
              <h3 className='relative z-[1] w-full text-[22px] font-medium leading-[1.2] text-text-primary min-[414px]:text-[24px] tabletH:text-green-deep05 min-[1024px]:text-[28px] min-[1180px]:text-[32px] desktopL:text-[48px]'>
                {isBusiness ? (
                  <>
                    인재의 준비는 탄탄하게,
                    <br />
                    기업의 채용은 수월하게.
                  </>
                ) : (
                  <>
                    <span className='block tabletH:hidden'>
                      {t('From Learning to', '배움에서 삶까지 —')}
                    </span>
                    <span className='block tabletH:hidden'>
                      {t(
                        'Living — Your Sustainable Career Journey Starts Here.',
                        '지속가능한 커리어 여정이 여기서 시작됩니다.',
                      )}
                    </span>
                    <span className='hidden tabletH:block'>
                      {t(
                        'From Readiness to Real Opportunities in Korea — Start Here.',
                        '준비에서 한국의 실제 기회까지 — 여기서 시작하세요.',
                      )}
                    </span>
                  </>
                )}
              </h3>
              <BaseButton
                type='text'
                size='s'
                typo={false}
                gap='gap-2.5'
                icon={<ButtonArrowIcon />}
                iconPosition='after'
                onClick={() => {
                  trackCtaClick({
                    button_name: 'get_now',
                    section: 'feature_cards',
                    page_path: window.location.pathname,
                  });
                  scroll('right');
                }}
                className='relative z-[1] h-auto min-w-0 rounded-[45px] border-0 bg-surface-gray60 px-4 py-3 text-grayscale-gray00 transition-colors hover:bg-surface-black60 hover:text-grayscale-gray00 min-[1024px]:px-[26px] min-[1024px]:py-4'
                textWrapperClass='h-auto flex-none text-[14px] font-medium leading-[1.4] min-[1024px]:text-[20px] min-[1024px]:leading-[1.2]'
              >
                {isBusiness ? '코레디 알아보기' : t('Get now')}
              </BaseButton>
            </article>
          </div>

          {slideCards.map(({ key, ...card }) => (
            <ArticleCard key={key} {...card} />
          ))}
          <div
            aria-hidden='true'
            className='pointer-events-none invisible hidden max-w-none shrink-0 snap-start tabletH:block tabletH:h-[352px] tabletH:w-[330px] tabletV:h-[400px] tabletV:w-[465px] min-[1180px]:h-[460px] min-[1180px]:w-[534px] desktopL:h-[600px] desktopL:w-[675px]'
          />
        </div>

        <div className='flex items-center justify-center w-full tabletH:hidden'>
          {Array.from({ length: mobileCardCount }, (_, index) => (
            <BaseButton
              key={index}
              type='text'
              size='s'
              isStatic
              aria-label={`Go to section card ${index + 1}`}
              onClick={() => scrollToIndex(index)}
              className={twMerge(
                'h-auto min-w-0 cursor-pointer border-0 bg-transparent px-1.5 py-2',
                activeMobileIndex === index ? 'w-9' : 'w-5',
              )}
              icon={
                <span
                  className={twMerge(
                    'h-2 rounded-xl transition-all',
                    activeMobileIndex === index
                      ? 'w-6 bg-grayscale-gray09'
                      : 'size-2 bg-grayscale-gray03',
                  )}
                />
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SustainableCareer;
